import { useCallback, useRef, useState } from "react";
import { PLANNING_OPERATION_MESSAGES } from "../../../../../config";
import { patchScheduleBlock } from "../../../../../services/api/schedule";
import { patchScopeAspects } from "../../../../../services/api/planning";
import {
  isLocalWeekend,
  toLocalCalendarDay,
  toLocalDateKey,
} from "../../../../../utils/datetime/core";
import {
  buildMapByNumericId,
  pickItemsByNumericIdsInOrder,
} from "../../../../../utils/collections/numericId";
import {
  mapAspectFromApi,
  mapBlockFromApi,
} from "../../model/scheduleApiMappers";
import {
  blockDateSpanHasDispatchEdited,
  blockDateSpanHasLockedDay,
  getAspectBlockId,
  sortedUniqueDateKeysFromScheduleAspects,
} from "../../model/scheduleBlockSelectors";
import { blockResizeTouchesLockedDay } from "../../model/scheduleLockGuards";
import {
  sameScheduleBlockId,
  sameScheduleProjectId,
} from "../../model/scheduleIds";
import { computeBlockMoveAfterDrop } from "../../model/scheduleMoveLogic";
import {
  materializeHiddenWeekendDragMove,
  mergeAspectDaysAfterBlockSpanChange,
  replaceBlockAspectRowsInFlatList,
} from "../../model/scheduleMaterializeAspectDays";
import {
  getScheduleCellDropPayload,
  readDndDataRef,
} from "../../dnd/scheduleDndResolveDrop";
import {
  buildFilteredWorkerIdsAfterConflictStrip,
  getWorkerConflictSummariesByDate,
} from "../../../shared/planningWorkerConflicts";

// =============================================================================
// Reviewed and approved
// =============================================================================

/**
 * Compare persisted placement fields only (job, group, start/end dates).
 */
function isSameBlockPlacement(before, after) {
  const beforeGroupId = before.scheduleGroup?.id ?? null;
  const afterGroupId = after.scheduleGroup?.id ?? null;
  return (
    sameScheduleProjectId(before.job?.id, after.job?.id) &&
    String(beforeGroupId ?? "") === String(afterGroupId ?? "") &&
    toLocalDateKey(before.startDate) === toLocalDateKey(after.startDate) &&
    toLocalDateKey(before.endDate) === toLocalDateKey(after.endDate)
  );
}

/**
 * Local day shifted by `offsetDays` (preserves schedule day-only semantics).
 *
 * @param {Date|string|number} day
 * @param {number} offsetDays
 * @returns {Date}
 */
function getShiftedCalendarDay(day, offsetDays) {
  const shifted = new Date(toLocalCalendarDay(day));
  shifted.setDate(shifted.getDate() + offsetDays);
  return shifted;
}

/**
 * Date keys for weekend days inside a block span, inclusive.
 *
 * @param {Object} block
 * @returns {Set<string>}
 */
function getWeekendDateKeysInBlockSpan(block) {
  const keys = new Set();
  const cursor = toLocalCalendarDay(block.startDate);
  const end = toLocalCalendarDay(block.endDate);
  while (cursor <= end) {
    if (isLocalWeekend(cursor)) {
      keys.add(toLocalDateKey(cursor));
    }
    cursor.setDate(cursor.getDate() + 1);
  }
  return keys;
}

/**
 * Check whether a block move introduces weekend dates that were not in the prior span.
 *
 * @param {Object} before
 * @param {Object} after
 * @returns {boolean}
 */
function blockDragAddsWeekendDates(before, after) {
  const beforeWeekendKeys = getWeekendDateKeysInBlockSpan(before);
  const afterWeekendKeys = getWeekendDateKeysInBlockSpan(after);
  for (const key of afterWeekendKeys) {
    if (!beforeWeekendKeys.has(key)) {
      return true;
    }
  }
  return false;
}

/**
 * Scope block mutations: catalog scope update, resize preview/commit, and drag/drop move.
 *
 * @param {Object} params
 * @param {import('react').MutableRefObject<Array<Object>>} params.blocksRef
 * @param {import('react').MutableRefObject<Array<Object>>} params.workScopeAspectsRef
 * @param {import('react').MutableRefObject<Array<Object>>} params.allJobsRef
 * @param {Array<Object>} params.visibleJobs
 * @param {Array<Object>} params.visibleGroups Groups in the visible window.
 * @param {Array<Object>} params.workScopes Work-scope catalog.
 * @param {Array<Object>|undefined} params.workers Crew catalog.
 * @param {boolean} params.showWeekends Whether Sat/Sun columns are visible on Schedule.
 * @param {Function} params.setBlocks
 * @param {Function} params.setWorkScopeAspects
 * @param {Function} params.setDragPreviewBlock Optimistic lane preview during drag.
 * @param {Function} params.runPlanningOperation
 * @param {() => Promise<void>} params.reloadScheduleBlocks Full block list refresh.
 * @param {Record<string, boolean>} [params.scheduleDayLocks] Dispatch day lock keys for the visible schedule range.
 * @param {(message: string, type?: 'success'|'info'|'error') => void} [params.notify] Planning toast (`PlanningContext.notify`).
 * @returns {Object} Settings/resize/drag modals, `persistBlockPatch`, and DnD handlers (see hook return).
 */
export function useScheduleBlockActions({
  blocksRef,
  workScopeAspectsRef,
  allJobsRef,
  visibleJobs,
  visibleGroups,
  workScopes,
  workers,
  showWeekends,
  setBlocks,
  setWorkScopeAspects,
  setDragPreviewBlock,
  runPlanningOperation,
  reloadScheduleBlocks,
  scheduleDayLocks = {},
  notify,
}) {
  const [scopeSettingsModalOpen, setScopeSettingsModalOpen] = useState(false);
  const [scopeSettingsBlock, setScopeSettingsBlock] = useState(null);
  const [resizePreview, setResizePreview] = useState(null);
  const [scopeBlockJoinModal, setScopeBlockJoinModal] = useState(null);

  /** Persist an edited block, then reconcile server canonical block/aspect data or roll back on failure. */
  const persistBlockPatch = useCallback(
    (
      blockId,
      patchBody,
      capturedBlockBefore,
      capturedAspectsBefore,
      options = {}
    ) => {
      void runPlanningOperation({
        module: "schedule",
        operation: "updateScheduleBlock",
        resources: ["schedule.blocks", "schedule.aspects"],
        apiCall: () => patchScheduleBlock(blockId, patchBody),
      })
        .then((raw) => {
          const mapped = mapBlockFromApi(raw);
          const mappedAspects = (raw.aspects ?? []).map((a) =>
            mapAspectFromApi(a, mapped.id)
          );
          setBlocks((prev) =>
            prev.map((b) => (sameScheduleBlockId(b.id, blockId) ? mapped : b))
          );
          setWorkScopeAspects((prev) => [
            ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
            ...mappedAspects,
          ]);
          const conflictSummaries = options.conflictSummaries ?? [];
          if (conflictSummaries.length > 0) {
            const catalog = workers ?? [];
            const byId = buildMapByNumericId(catalog);
            const conflictPatchRows = [];
            for (const { dateKey, conflictWorkerIds } of conflictSummaries) {
              const aspect = mappedAspects.find(
                (a) => toLocalDateKey(a.date) === dateKey
              );
              if (!aspect || !/^\d+$/.test(String(aspect.id))) {
                continue;
              }
              const workerIds = buildFilteredWorkerIdsAfterConflictStrip({
                block: mapped,
                aspect,
                conflictWorkerIds,
              });
              conflictPatchRows.push({
                scopeId: Number(aspect.id),
                workerIds,
                idKey: String(aspect.id),
              });
            }
            if (conflictPatchRows.length > 0) {
              void runPlanningOperation({
                module: "schedule",
                operation: "updateScheduleConflictCrewOverrides",
                resources: ["schedule.aspects"],
                apiCall: () =>
                  patchScopeAspects(
                    conflictPatchRows.map(({ scopeId, workerIds }) => ({
                      scopeId,
                      workerIds,
                    })),
                    undefined,
                    "schedule"
                  ),
              }).then(() => {
                const nextAssignmentsByAspect = new Map(
                  conflictPatchRows.map(({ idKey, workerIds }) => [
                    idKey,
                    pickItemsByNumericIdsInOrder(byId, workerIds),
                  ])
                );
                setWorkScopeAspects((prev) =>
                  prev.map((row) => {
                    const nextAssignment = nextAssignmentsByAspect.get(String(row.id));
                    return nextAssignment
                      ? { ...row, worker_assignment: nextAssignment }
                      : row;
                  })
                );
              });
            }
          }
        })
        .catch(async (err) => {
          const errorCode = err?.response?.data?.code;
          if (
            (errorCode === "schedule_merge_confirmation_required" ||
              errorCode?.[0] === "schedule_merge_confirmation_required") &&
            typeof options.onMergeConfirmationRequired === "function"
          ) {
            options.onMergeConfirmationRequired();
            return;
          }
          console.error("Schedule block save failed", err);
          // If the write reached the backend but the client request failed late, prefer
          // canonical server state over stale optimistic rollback.
          if (typeof reloadScheduleBlocks === "function") {
            try {
              await reloadScheduleBlocks();
              return;
            } catch (reloadErr) {
              console.error("Schedule block recovery reload failed", reloadErr);
            }
          }
          if (!capturedBlockBefore || capturedAspectsBefore == null) {
            return;
          }
          setBlocks((prev) =>
            prev.map((b) =>
              sameScheduleBlockId(b.id, blockId) ? { ...capturedBlockBefore } : b
            )
          );
          setWorkScopeAspects((prev) => [
            ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
            ...capturedAspectsBefore,
          ]);
        });
    },
    [
      reloadScheduleBlocks,
      runPlanningOperation,
      setBlocks,
      setWorkScopeAspects,
      workers,
    ]
  );

  /** Pending block drag when user must confirm through the worker-conflict modal. */
  const workerDragConflictPendingRef = useRef(null);
  /** Conflict rows for modal UI only (`null` = closed). */
  const [workerDragConflictModal, setWorkerDragConflictModal] = useState(null);
  /** Deferred Schedule action waiting for dispatch-edited warning confirmation. */
  const dispatchEditedWarningPendingRef = useRef(null);
  /** Dispatch-edited warning modal state (`null` = closed). */
  const [dispatchEditedWarningModal, setDispatchEditedWarningModal] = useState(null);

  /**
   * Open the dispatch-edited warning modal and stage the deferred action.
   *
   * @param {{ onConfirm: () => void, deleteWarning?: boolean }} params
   * @returns {void}
   */
  const openDispatchEditedWarningModal = useCallback(
    ({ onConfirm, deleteWarning = false }) => {
      dispatchEditedWarningPendingRef.current = onConfirm;
      setDispatchEditedWarningModal({ deleteWarning: Boolean(deleteWarning) });
    },
    []
  );

  /** Confirm dispatch-edited warning and execute staged mutation action. */
  const confirmDispatchEditedWarningModal = useCallback(() => {
    const pending = dispatchEditedWarningPendingRef.current;
    dispatchEditedWarningPendingRef.current = null;
    setDispatchEditedWarningModal(null);
    pending?.();
  }, []);

  /** Cancel dispatch-edited warning and drop staged mutation action. */
  const cancelDispatchEditedWarningModal = useCallback(() => {
    dispatchEditedWarningPendingRef.current = null;
    setDispatchEditedWarningModal(null);
  }, []);

  const cancelWorkerDragConflictModal = useCallback(() => {
    const pending = workerDragConflictPendingRef.current;
    workerDragConflictPendingRef.current = null;
    setWorkerDragConflictModal(null);
    if (pending?.mode === "custom") {
      pending.onCancel?.();
    }
  }, []);

  /**
   * Reuse the shared worker-conflict confirmation modal for any schedule mutation.
   *
   * @param {Object} params
   * @param {Array<Object>} params.conflictSummaries Modal rows from `getWorkerConflictSummariesByDate`.
   * @param {() => void|Promise<void>} params.onConfirm Called when user confirms.
   * @param {() => void|Promise<void>} [params.onCancel] Called when user cancels.
   */
  const requestWorkerConflictConfirmation = useCallback(
    ({ conflictSummaries, onConfirm, onCancel }) => {
      const conflictOnly = Array.isArray(conflictSummaries)
        ? conflictSummaries.filter(
            (summary) => (summary?.conflictNames?.length ?? 0) > 0
          )
        : [];
      if (conflictOnly.length === 0) {
        onConfirm?.();
        return;
      }
      workerDragConflictPendingRef.current = {
        mode: "custom",
        onConfirm,
        onCancel,
      };
      setWorkerDragConflictModal(conflictOnly);
    },
    []
  );

  const commitPendingBlockDragMove = useCallback(
    (pending) => {
      if (!pending) {
        return;
      }
      const {
        blockId,
        nextBlock,
        patchBody,
        nextFor,
        capturedBlockBefore,
        capturedAspectsBefore,
      } = pending;
      setBlocks((prev) =>
        prev.map((b) => (sameScheduleBlockId(b.id, blockId) ? nextBlock : b))
      );
      setWorkScopeAspects((prev) => [
        ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
        ...nextFor,
      ]);
      persistBlockPatch(
        blockId,
        patchBody,
        capturedBlockBefore,
        capturedAspectsBefore
      );
    },
    [persistBlockPatch, setBlocks, setWorkScopeAspects]
  );

  const confirmWorkerDragConflictModal = useCallback(() => {
    const pending = workerDragConflictPendingRef.current;
    workerDragConflictPendingRef.current = null;
    setWorkerDragConflictModal(null);
    if (!pending) {
      return;
    }
    if (pending.mode === "custom") {
      pending.onConfirm?.();
      return;
    }
    const conflictSummaries = pending.conflictSummaries;
    if (!Array.isArray(conflictSummaries) || conflictSummaries.length === 0) {
      commitPendingBlockDragMove(pending);
      return;
    }

    const {
      blockId,
      patchBody,
      capturedBlockBefore,
      capturedAspectsBefore,
    } = pending;

    void (async () => {
      try {
        const raw = await runPlanningOperation({
          module: "schedule",
          operation: "moveScheduleBlock",
          resources: ["schedule.blocks", "schedule.aspects"],
          apiCall: () => patchScheduleBlock(blockId, patchBody),
        });
        const mappedBlock = mapBlockFromApi(raw);
        const mappedAspects = (raw.aspects ?? []).map((a) =>
          mapAspectFromApi(a, mappedBlock.id)
        );
        setBlocks((prev) =>
          prev.map((b) =>
            sameScheduleBlockId(b.id, blockId) ? mappedBlock : b
          )
        );
        setWorkScopeAspects((prev) => [
          ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
          ...mappedAspects,
        ]);

        const catalog = workers ?? [];
        const byId = buildMapByNumericId(catalog);

        const conflictPatchRows = [];
        for (const { dateKey, conflictWorkerIds } of conflictSummaries) {
          const aspect = mappedAspects.find(
            (a) => toLocalDateKey(a.date) === dateKey
          );
          if (!aspect || !/^\d+$/.test(String(aspect.id))) {
            continue;
          }
          const workerIds = buildFilteredWorkerIdsAfterConflictStrip({
            block: mappedBlock,
            aspect,
            conflictWorkerIds,
          });
          conflictPatchRows.push({
            scopeId: Number(aspect.id),
            workerIds,
            idKey: String(aspect.id),
          });
        }
        if (conflictPatchRows.length > 0) {
          try {
            await runPlanningOperation({
              module: "schedule",
              operation: "updateScheduleConflictCrewOverrides",
              resources: ["schedule.aspects"],
              apiCall: () =>
                patchScopeAspects(
                  conflictPatchRows.map(({ scopeId, workerIds }) => ({
                    scopeId,
                    workerIds,
                  })),
                  undefined,
                  "schedule"
                ),
            });
            const nextAssignmentsByAspect = new Map(
              conflictPatchRows.map(({ idKey, workerIds }) => [
                idKey,
                pickItemsByNumericIdsInOrder(byId, workerIds),
              ])
            );
            setWorkScopeAspects((prev) =>
              prev.map((row) => {
                const nextAssignment = nextAssignmentsByAspect.get(String(row.id));
                return nextAssignment
                  ? { ...row, worker_assignment: nextAssignment }
                  : row;
              })
            );
          } catch (aspectErr) {
            console.error("Schedule conflict crew override batch failed", aspectErr);
          }
        }
      } catch (err) {
        console.error("Schedule block move failed after conflict confirm", err);
        setBlocks((prev) =>
          prev.map((b) =>
            sameScheduleBlockId(b.id, blockId) ? { ...capturedBlockBefore } : b
          )
        );
        setWorkScopeAspects((prev) => [
          ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
          ...capturedAspectsBefore,
        ]);
      }
    })();
  }, [
    commitPendingBlockDragMove,
    setBlocks,
    setWorkScopeAspects,
    runPlanningOperation,
    workers,
  ]);

  // --- Reviewed and approved (depends on persistBlockPatch above; promote persist when reviewed) ---

  /** Resolve a drag event to the same projected move payload used by drop commit. */
  const resolveBlockDragMove = useCallback(
    (event) => {
      const { active } = event;
      const drop = getScheduleCellDropPayload(event);
      if (!drop) {
        return null;
      }
      const activeData = readDndDataRef(active?.data);
      const blockId = activeData?.blockId;
      if (blockId == null) {
        return null;
      }
      const block = blocksRef.current.find((b) =>
        sameScheduleBlockId(b.id, blockId)
      );
      if (!block) {
        return null;
      }
      const targetJobRaw = allJobsRef.current.find((j) =>
        sameScheduleProjectId(j.id, drop.row.projectId)
      );
      const targetProject = visibleJobs.find((p) =>
        sameScheduleProjectId(p.id, drop.row.projectId)
      );
      const nextBlockJob = targetProject
        ? {
          id: targetProject.id,
          number: targetProject.projectNumber,
          description: targetProject.name,
        }
        : targetJobRaw
          ? {
            id: targetJobRaw.id,
            number: targetJobRaw.number ?? targetJobRaw.project_number ?? "",
            description: targetJobRaw.description ?? "",
          }
          : null;
      if (!nextBlockJob) {
        return null;
      }
      let groupMeta = null;
      if (drop.row.rowType === "group") {
        const g = visibleGroups.find(
          (x) => String(x.id) === String(drop.row.groupId)
        );
        groupMeta = g ? { id: g.id } : { id: drop.row.groupId };
      }

      const grabDayKey = activeData?.grabDayKey ?? null;
      let { nextBlock, patchBody } = computeBlockMoveAfterDrop({
        block,
        targetRow: drop.row,
        dropDay: drop.day,
        targetJobRaw,
        groupMeta: drop.row.rowType === "group" ? groupMeta : null,
        targetJobId: drop.row.projectId,
        nextBlockJob,
        grabDayKey,
      });
      patchBody.preserve_skipped_weekends_by_weekday = !showWeekends;
      if (!showWeekends && blockDragAddsWeekendDates(block, nextBlock)) {
        const prevFor = workScopeAspectsRef.current.filter((d) =>
          sameScheduleBlockId(getAspectBlockId(d), blockId)
        );
        const materialized = materializeHiddenWeekendDragMove(nextBlock, prevFor);
        if (materialized.nextAspects.length > 0) {
          nextBlock = materialized.nextBlock;
          patchBody = {
            ...patchBody,
            end_date: toLocalDateKey(nextBlock.endDate),
          };
        }
      }
      if (isSameBlockPlacement(block, nextBlock)) {
        return null;
      }
      return { blockId, block, nextBlock, patchBody, drop };
    },
    [
      allJobsRef,
      blocksRef,
      showWeekends,
      visibleGroups,
      visibleJobs,
      workScopeAspectsRef,
    ]
  );

  /** Preview row expansion/lane placement while dragging over a valid drop cell. */
  const handleBlockDragOver = useCallback(
    (event) => {
      const projected = resolveBlockDragMove(event);
      if (!projected) {
        setDragPreviewBlock(null);
        return;
      }
      setDragPreviewBlock(projected.nextBlock);
    },
    [resolveBlockDragMove, setDragPreviewBlock]
  );

  /** Clear transient drag lane preview when dnd-kit cancels a drag. */
  const handleBlockDragCancel = useCallback(() => {
    setDragPreviewBlock(null);
  }, [setDragPreviewBlock]);

  /** Finalize block drag/drop into a target row/day and persist resulting move. */
  const handleBlockDragEnd = useCallback(
    (event) => {
      setDragPreviewBlock(null);
      const projected = resolveBlockDragMove(event);
      if (!projected) {
        return;
      }
      let { blockId, block, nextBlock, patchBody, drop } = projected;
      const prevAspects = workScopeAspectsRef.current;
      const prevFor = prevAspects.filter((d) =>
        sameScheduleBlockId(getAspectBlockId(d), blockId)
      );
      let nextFor = null;

      if (!showWeekends && blockDragAddsWeekendDates(block, nextBlock)) {
        // Hidden weekends would make the dropped bar look shorter. Insert skipped
        // weekend filler aspects and push following work aspects forward so visible
        // weekday length stays the same after the backend save.
        const materialized = materializeHiddenWeekendDragMove(nextBlock, prevFor);
        if (materialized.nextAspects.length > 0) {
          nextBlock = materialized.nextBlock;
          nextFor = materialized.nextAspects;
          patchBody = {
            ...patchBody,
            end_date: toLocalDateKey(nextBlock.endDate),
          };
        }
      }

      if (blockDateSpanHasLockedDay(nextBlock, scheduleDayLocks)) {
        notify?.(
          PLANNING_OPERATION_MESSAGES.scheduleCannotMoveScopeIntoLockedDays,
          "error"
        );
        return;
      }

      const dispatchEditedInBlock = blockDateSpanHasDispatchEdited({
        ...block,
        aspects: prevFor,
      });
      const capturedAspectsBefore = prevFor.map((d) => ({ ...d }));
      const capturedBlockBefore = { ...block };
      if (nextFor == null) {
        nextFor = mergeAspectDaysAfterBlockSpanChange(nextBlock, prevFor, {
          pairBySortedIndexOnEqualLength: true,
          preserveSkippedWeekendsByWeekday: !showWeekends,
        });
      }

      if (dispatchEditedInBlock) {
        openDispatchEditedWarningModal({
          deleteWarning: false,
          onConfirm: () => {
            const spanDatesChangedAfterConfirm =
              toLocalDateKey(block.startDate) !== toLocalDateKey(nextBlock.startDate) ||
              toLocalDateKey(block.endDate) !== toLocalDateKey(nextBlock.endDate);
            if (spanDatesChangedAfterConfirm && nextFor.length > 0) {
              const merged = replaceBlockAspectRowsInFlatList(
                prevAspects,
                blockId,
                nextFor
              );
              const dateKeys = sortedUniqueDateKeysFromScheduleAspects(nextFor);
              const summaries = getWorkerConflictSummariesByDate(merged, dateKeys, {
                workers,
              });
              const conflictOnly = summaries.filter(
                (s) => s.conflictNames.length > 0
              );
              if (conflictOnly.length > 0) {
                workerDragConflictPendingRef.current = {
                  mode: "blockDrag",
                  blockId,
                  nextBlock,
                  patchBody,
                  nextFor,
                  capturedBlockBefore,
                  capturedAspectsBefore,
                  dropRow: drop.row,
                  conflictSummaries: conflictOnly,
                };
                setWorkerDragConflictModal(conflictOnly);
                return;
              }
            }

            commitPendingBlockDragMove({
              blockId,
              nextBlock,
              patchBody,
              nextFor,
              capturedBlockBefore,
              capturedAspectsBefore,
              dropRow: drop.row,
            });
          },
        });
        return;
      }

      const spanDatesChanged =
        toLocalDateKey(block.startDate) !== toLocalDateKey(nextBlock.startDate) ||
        toLocalDateKey(block.endDate) !== toLocalDateKey(nextBlock.endDate);
      if (spanDatesChanged && nextFor.length > 0) {
        const merged = replaceBlockAspectRowsInFlatList(
          prevAspects,
          blockId,
          nextFor
        );
        const dateKeys = sortedUniqueDateKeysFromScheduleAspects(nextFor);
        const summaries = getWorkerConflictSummariesByDate(merged, dateKeys, {
          workers,
        });
        const conflictOnly = summaries.filter((s) => s.conflictNames.length > 0);
        if (conflictOnly.length > 0) {
          workerDragConflictPendingRef.current = {
            mode: "blockDrag",
            blockId,
            nextBlock,
            patchBody,
            nextFor,
            capturedBlockBefore,
            capturedAspectsBefore,
            dropRow: drop.row,
            conflictSummaries: conflictOnly,
          };
          setWorkerDragConflictModal(conflictOnly);
          return;
        }
      }

      commitPendingBlockDragMove({
        blockId,
        nextBlock,
        patchBody,
        nextFor,
        capturedBlockBefore,
        capturedAspectsBefore,
        dropRow: drop.row,
      });
    },
    [
      commitPendingBlockDragMove,
      notify,
      openDispatchEditedWarningModal,
      resolveBlockDragMove,
      scheduleDayLocks,
      setDragPreviewBlock,
      showWeekends,
      workers,
      workScopeAspectsRef,
    ]
  );

  // --- Not yet reviewed (hook body) ---

  /** Open scope settings modal for the selected timeline block. */
  const openScopeSettings = useCallback((block) => {
    if (!block) {
      return;
    }
    setScopeSettingsBlock(block);
    setScopeSettingsModalOpen(true);
  }, []);

  /** Close scope settings modal and clear selected block context. */
  const closeScopeSettingsModal = useCallback(() => {
    setScopeSettingsModalOpen(false);
    setScopeSettingsBlock(null);
  }, []);

  /** Apply scope catalog change to the selected block with optimistic update + persistence. */
  const handleConfirmScopeSettings = useCallback(
    ({ workScopeId }) => {
      const block = scopeSettingsBlock;
      if (!block?.id) {
        return;
      }
      const idStr = String(block.id);
      if (!/^\d+$/.test(idStr)) {
        return;
      }
      const catalogRow = workScopes.find(
        (w) => String(w.id) === String(workScopeId)
      );
      if (!catalogRow) {
        return;
      }
      const blockId = block.id;
      const capturedBlockBefore = { ...block };
      const prevFor = workScopeAspectsRef.current
        .filter((d) => sameScheduleBlockId(getAspectBlockId(d), blockId))
        .map((d) => ({ ...d }));
      const capturedAspectsBefore = prevFor;

      setBlocks((prev) =>
        prev.map((b) =>
          sameScheduleBlockId(b.id, blockId)
            ? {
              ...b,
              scope: { id: catalogRow.id, name: catalogRow.name },
            }
            : b
        )
      );

      persistBlockPatch(
        blockId,
        { work_scope_id: catalogRow.id },
        capturedBlockBefore,
        capturedAspectsBefore
      );
      closeScopeSettingsModal();
    },
    [
      closeScopeSettingsModal,
      persistBlockPatch,
      scopeSettingsBlock,
      setBlocks,
      workScopes,
      workScopeAspectsRef,
    ]
  );

  /** Store transient resize preview payload while dragging a block edge. */
  const handleResizePreview = useCallback((payload) => {
    setResizePreview(payload);
  }, []);

  const confirmScopeBlockJoinModal = useCallback(() => {
    const pending = scopeBlockJoinModal;
    setScopeBlockJoinModal(null);
    if (!pending) {
      return;
    }
    persistBlockPatch(
      pending.blockId,
      {
        start_date: toLocalDateKey(pending.capturedNextBlock.startDate),
        end_date: toLocalDateKey(pending.capturedNextBlock.endDate),
        schedule_group_id: pending.capturedNextBlock.scheduleGroup?.id ?? null,
        skip_created_weekend_aspect_days: Boolean(
          pending.skipCreatedWeekendAspectDays
        ),
      },
      pending.capturedBlockBefore,
      pending.capturedAspectsBefore,
      { conflictSummaries: pending.conflictSummaries ?? [] }
    );
  }, [persistBlockPatch, scopeBlockJoinModal]);

  const cancelScopeBlockJoinModal = useCallback(() => {
    const pending = scopeBlockJoinModal;
    setScopeBlockJoinModal(null);
    if (!pending) {
      return;
    }
    const draggedDateKey = pending.edge === "start" ? "start_date" : "end_date";
    const draggedDate =
      pending.edge === "start"
        ? pending.capturedNextBlock.startDate
        : pending.capturedNextBlock.endDate;
    persistBlockPatch(
      pending.blockId,
      {
        [draggedDateKey]: toLocalDateKey(draggedDate),
        merge_adjacent_blocks: false,
        skip_created_weekend_aspect_days: Boolean(
          pending.skipCreatedWeekendAspectDays
        ),
      },
      pending.capturedBlockBefore,
      pending.capturedAspectsBefore,
      { conflictSummaries: pending.conflictSummaries ?? [] }
    );
  }, [persistBlockPatch, scopeBlockJoinModal]);

  /** Commit block span resize, regenerate aspect days, and persist block date updates. */
  const handleResizeCommit = useCallback(
    (blockId, startDate, endDate, edge) => {
      setResizePreview(null);

      const block = blocksRef.current.find((b) =>
        sameScheduleBlockId(b.id, blockId)
      );
      if (!block) {
        return;
      }

      const capturedBlockBefore = { ...block };

      if (edge === "start") {
        const s = capturedBlockBefore.startDate;
        const e = startDate;
        if (s.getTime() === e.getTime()) {
          return;
        }
      } else if (edge === "end") {
        const s = capturedBlockBefore.endDate;
        const e = endDate;
        if (s.getTime() === e.getTime()) {
          return;
        }
      }

      const capturedNextBlock = {
        ...block,
        startDate,
        endDate,
      };
      const touchesLockedDay = blockResizeTouchesLockedDay(
        capturedBlockBefore,
        capturedNextBlock,
        edge,
        scheduleDayLocks
      );
      if (touchesLockedDay) {
        notify?.(
          PLANNING_OPERATION_MESSAGES.scheduleCannotChangeScopeDatesDueToLockedDays,
          "error"
        );
        return;
      }

      const prevAspects = workScopeAspectsRef.current;
      const prevFor = prevAspects.filter((d) =>
        sameScheduleBlockId(getAspectBlockId(d), blockId)
      );
      const capturedAspectsBefore = prevFor.map((d) => ({ ...d }));
      const skipCreatedWeekendAspectDays = !showWeekends;
      const nextFor = mergeAspectDaysAfterBlockSpanChange(capturedNextBlock, prevFor, {
        skipNewWeekendAspectDays: skipCreatedWeekendAspectDays,
      });

      const pending = {
        blockId,
        capturedNextBlock,
        capturedBlockBefore,
        capturedAspectsBefore,
        edge,
        skipCreatedWeekendAspectDays,
      };
      const merged = replaceBlockAspectRowsInFlatList(
        prevAspects,
        blockId,
        nextFor
      );
      const conflictOnly = getWorkerConflictSummariesByDate(
        merged,
        sortedUniqueDateKeysFromScheduleAspects(nextFor),
        { workers }
      ).filter((summary) => summary.conflictWorkerIds.length > 0);

      /** Apply resize optimistic state + persist with merge-confirm flow. */
      const commitResizeChange = (conflictSummaries = []) => {
        setBlocks((prev) =>
          prev.map((b) => (sameScheduleBlockId(b.id, blockId) ? capturedNextBlock : b))
        );
        setWorkScopeAspects((prev) => [
          ...prev.filter((d) => !sameScheduleBlockId(getAspectBlockId(d), blockId)),
          ...nextFor,
        ]);

        persistBlockPatch(
          blockId,
          {
            start_date: toLocalDateKey(capturedNextBlock.startDate),
            end_date: toLocalDateKey(capturedNextBlock.endDate),
            schedule_group_id: capturedNextBlock.scheduleGroup?.id ?? null,
            require_merge_confirmation: true,
            skip_created_weekend_aspect_days: skipCreatedWeekendAspectDays,
          },
          capturedBlockBefore,
          capturedAspectsBefore,
          {
            onMergeConfirmationRequired: () =>
              setScopeBlockJoinModal({ ...pending, conflictSummaries }),
            conflictSummaries,
          }
        );
      };

      const oldStartKey = toLocalDateKey(capturedBlockBefore.startDate);
      const oldEndKey = toLocalDateKey(capturedBlockBefore.endDate);
      const newStartKey = toLocalDateKey(capturedNextBlock.startDate);
      const newEndKey = toLocalDateKey(capturedNextBlock.endDate);
      const blockForDispatchCheck = {
        ...capturedBlockBefore,
        aspects: prevFor,
      };

      if (edge === "start" && newStartKey > oldStartKey) {
        const deleteRangeStart = capturedBlockBefore.startDate;
        const deleteRangeEnd = getShiftedCalendarDay(capturedNextBlock.startDate, -1);
        const deletesDispatchEditedDays = blockDateSpanHasDispatchEdited(
          blockForDispatchCheck,
          deleteRangeStart,
          deleteRangeEnd
        );
        if (deletesDispatchEditedDays) {
          openDispatchEditedWarningModal({
            deleteWarning: true,
            onConfirm: () => commitResizeChange(conflictOnly),
          });
          return;
        }
      }

      if (edge === "end" && newEndKey < oldEndKey) {
        const deleteRangeStart = getShiftedCalendarDay(capturedNextBlock.endDate, 1);
        const deleteRangeEnd = capturedBlockBefore.endDate;
        const deletesDispatchEditedDays = blockDateSpanHasDispatchEdited(
          blockForDispatchCheck,
          deleteRangeStart,
          deleteRangeEnd
        );
        if (deletesDispatchEditedDays) {
          openDispatchEditedWarningModal({
            deleteWarning: true,
            onConfirm: () => commitResizeChange(conflictOnly),
          });
          return;
        }
      }

      if (conflictOnly.length > 0) {
        requestWorkerConflictConfirmation({
          conflictSummaries: conflictOnly,
          onConfirm: () => commitResizeChange(conflictOnly),
        });
        return;
      }

      commitResizeChange();
    },
    [
      blocksRef,
      openDispatchEditedWarningModal,
      notify,
      persistBlockPatch,
      requestWorkerConflictConfirmation,
      scheduleDayLocks,
      setBlocks,
      setWorkScopeAspects,
      showWeekends,
      workers,
      workScopeAspectsRef,
    ]
  );

  return {
    scopeSettingsModalOpen,
    scopeSettingsBlock,
    resizePreview,
    scopeBlockJoinModal,
    persistBlockPatch,
    openScopeSettings,
    closeScopeSettingsModal,
    handleConfirmScopeSettings,
    handleResizePreview,
    handleResizeCommit,
    confirmScopeBlockJoinModal,
    cancelScopeBlockJoinModal,
    handleBlockDragOver,
    handleBlockDragEnd,
    handleBlockDragCancel,
    dispatchEditedWarningModal,
    confirmDispatchEditedWarningModal,
    cancelDispatchEditedWarningModal,
    requestWorkerConflictConfirmation,
    workerDragConflictModal,
    confirmWorkerDragConflictModal,
    cancelWorkerDragConflictModal,
  };
}
